import React, { useState, useRef, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/ui/components/accordion';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/ui/components/inputGroup';
import { ChevronRightIcon, SearchIcon } from '@/ui/icons';
import { cn } from '@/ui/lib/utils';
import type { Category } from '@/common/models/categories/category';
import { handleHttpError } from '@/common/http/errorHandler';
import Loader from '../common/loader';
import type { Company } from '../../models/companies/company';
import type { Area } from '../../models/companies/areas/area';
import type { Feature } from '../../models/companies/features/feature';
import type { EcfStage } from '../../models/companies/ecf_stages/ecfStage';
import * as companyClient from '../../models/companies/companyClient';
import { CompanyCard } from './companyCard';
import {
  buildRansackQuery,
  fundingBefores,
  fundingAfters,
  exitExperiences,
  fundingSchemes,
  angelTaxes,
  sdgs,
  ecfFundings,
} from '../../models/companies/company';
import { useSearchInput } from '../../hooks/useSearchInput';

interface Props {
  categories: Category[];
  areas: Area[];
  features: Feature[];
  ecfStages: EcfStage[];
}

// 選択肢の行。Tailwind Preflight を読み込んでいないため、button の
// UA 既定（枠線・背景・フォント）は個別に打ち消す（AccordionTrigger と同じ方針）。
// 区切り線は枠いっぱいに引くため、余白は Panel ではなく行側に持たせる。
const rowClassName = cn(
  'tw:m-0 tw:flex tw:h-14 tw:w-full tw:cursor-pointer tw:items-center tw:justify-between tw:gap-2',
  'tw:border-x-0 tw:border-b-0 tw:border-t tw:border-solid tw:border-border tw:bg-transparent',
  'tw:px-4 tw:text-left tw:text-base tw:font-bold',
  // Panel が overflow-hidden のため、リングは外側でなく内側に描く（DS の FocusedRow story と同じ）
  'tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-inset tw:focus-visible:ring-ring',
);
export default function IndexView(props: Props) {
  const { categories, areas, features, ecfStages } = props;
  const [companies, setCompanies] = useState<Company[]>([]);
  const [query, setQuery] = useState({ keyword: '', page: 1 });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [fundingBefore, setFundingBefore] = useState<number | null>(null);
  const [fundingAfter, setFundingAfter] = useState<number | null>(null);
  const [exitExperience, setExitExperience] = useState<number | null>(null);
  const [category, setCategory] = useState<number | null>(null);
  const [area, setArea] = useState<number | null>(null);
  const [fundingScheme, setFundingScheme] = useState<number | null>(null);
  const [angelTax, setAngelTax] = useState<number | null>(null);
  const [sdg, setSdg] = useState<number | null>(null);
  const [feature, setFeature] = useState<number | null>(null);
  const [ecfStage, setEcfStage] = useState<number | null>(null);
  const [ecfFunding, setEcfFunding] = useState<number | null>(null);

  const conditions = [
    {
      key: 'fundingBefore',
      name: 'FUNDINNO利用前調達',
      items: fundingBefores,
      activeItemId: fundingBefore,
      className: 'funding-before',
    },
    {
      key: 'fundingAfter',
      name: 'FUNDINNO利用後調達',
      items: fundingAfters,
      activeItemId: fundingAfter,
      className: 'funding-after',
    },
    {
      key: 'exitExperience',
      name: 'EXIT実績',
      items: exitExperiences,
      activeItemId: exitExperience,
      className: 'exit-experience',
    },
    {
      key: 'sdg',
      name: 'SDGsへの取り組み',
      items: sdgs,
      activeItemId: sdg,
      className: 'sdgs',
    },
    {
      key: 'category',
      name: '業種',
      items: categories,
      activeItemId: category,
      className: 'categories',
    },
    {
      key: 'ecfFunding',
      name: 'ECF複数回調達実績',
      items: ecfFundings,
      activeItemId: ecfFunding,
      className: 'ecf-funding',
    },
    {
      key: 'area',
      name: '地域',
      items: areas,
      activeItemId: area,
      className: 'areas',
    },
    {
      key: 'fundingScheme',
      name: '調達スキーム',
      items: fundingSchemes,
      activeItemId: fundingScheme,
      className: 'funding-schemes',
    },
    {
      key: 'angelTax',
      name: 'エンジェル税制',
      items: angelTaxes,
      activeItemId: angelTax,
      className: 'angel-tax',
    },
    {
      key: 'ecfStage',
      name: 'ECF利用時のステージ',
      items: ecfStages,
      activeItemId: ecfStage,
      className: 'ecf-stages',
    },
    {
      key: 'feature',
      name: '企業の特徴',
      items: features,
      activeItemId: feature,
      className: 'features',
    },
  ] as const;

  type ConditionKey = (typeof conditions)[number]['key'];

  const abortController = useRef<AbortController | null>(null);

  const invalidateRequest = () => {
    abortController.current?.abort();
    abortController.current = null;
  };

  const search = useSearchInput({
    initialValue: '',
    onCommit: keyword => {
      invalidateRequest();
      setCompanies([]);
      setIsLoading(true);
      setHasNextPage(true);
      setQuery({ keyword, page: 1 });
    },
  });

  const fetchCompanies = () => {
    const builtQuery = buildRansackQuery(
      query.keyword,
      fundingBefore,
      fundingAfter,
      exitExperience,
      category,
      area,
      fundingScheme,
      angelTax,
      sdg,
      feature,
      ecfStage,
      ecfFunding,
    );
    // 直前の未完了リクエストを中断し、結果の競合（race condition）を防ぐ
    invalidateRequest();
    const controller = new AbortController();
    abortController.current = controller;
    companyClient
      .searchCompanies(query.page, builtQuery, controller.signal)
      .then(({ items, hasNext }) => {
        // 1ページ目（新規検索・条件変更）は置換、2ページ目以降（追加読み込み）は追記
        if (abortController.current !== controller || controller.signal.aborted)
          return;
        setCompanies(prev => (query.page === 1 ? items : [...prev, ...items]));
        setHasNextPage(hasNext);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        // 中断されたリクエストは後続のリクエストに引き継ぐため何もしない
        if (abortController.current !== controller || controller.signal.aborted)
          return;
        handleHttpError(error);
        // エラー時はローディングを解除しつつ追加読み込みも止め、
        // UI の固着と InfiniteScroll による再取得ループを防ぐ
        setIsLoading(false);
        setHasNextPage(false);
      });
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react-hooks ルール有効化に伴う既存実装の暫定許容（別途対応）
  }, [
    fundingBefore,
    fundingAfter,
    exitExperience,
    category,
    area,
    fundingScheme,
    angelTax,
    sdg,
    feature,
    ecfStage,
    ecfFunding,
    // 同じ検索語の再送（再試行）でも再取得できるよう、field ではなく object の identity で追う
    query,
  ]);

  useEffect(() => {
    return () => {
      // アンマウント時に未完了リクエストを中断し、解放後の state 更新を防ぐ
      invalidateRequest();
    };
  }, []);

  const onClickCondition = (name: ConditionKey, id: number | null) => {
    if (!isLoading) {
      invalidateRequest();
      setCompanies([]);
      setQuery(current => ({ ...current, page: 1 }));
      setIsLoading(true);
      setHasNextPage(true);
      switch (name) {
        case 'fundingBefore':
          setFundingBefore(id);
          break;
        case 'fundingAfter':
          setFundingAfter(id);
          break;
        case 'exitExperience':
          setExitExperience(id);
          break;
        case 'category':
          setCategory(id);
          break;
        case 'area':
          setArea(id);
          break;
        case 'fundingScheme':
          setFundingScheme(id);
          break;
        case 'angelTax':
          setAngelTax(id);
          break;
        case 'sdg':
          setSdg(id);
          break;
        case 'feature':
          setFeature(id);
          break;
        case 'ecfStage':
          setEcfStage(id);
          break;
        case 'ecfFunding':
          setEcfFunding(id);
          break;
        default:
          break;
      }
    }
  };

  const onLoadMore = () => {
    if (!isLoading && hasNextPage) {
      setQuery(current => ({ ...current, page: current.page + 1 }));
      setIsLoading(true);
    }
  };

  return (
    <>
      <div className="row mb-20">
        <div className="col-md-4 mb-20">
          <h1>調達企業一覧</h1>
        </div>
        <div className="col-md-8 mb-20">
          <div className="ui-root">
            <form
              onSubmit={event => {
                event.preventDefault();
                search.submit();
              }}
            >
              <InputGroup variant="plain">
                <InputGroupInput
                  {...search.inputProps}
                  type="text"
                  name="keyword"
                  maxLength={50}
                  placeholder="検索"
                />
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
              </InputGroup>
            </form>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="ui-root col-md-4 side-menu mb-40">
          <Accordion multiple className="tw:gap-0">
            {conditions.map(condition => (
              <AccordionItem
                key={condition.key}
                value={condition.key}
                className={`${condition.className} mb-3`}
              >
                {/* ページの h1 の直下なので、絞り込みの見出しは h2 に揃える。 */}
                <AccordionTrigger headingLevel="h2">
                  {condition.name}
                </AccordionTrigger>
                <AccordionContent>
                  {condition.items.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className={cn(
                        rowClassName,
                        condition.activeItemId === item.id
                          ? 'tw:text-primary'
                          : 'tw:text-inherit',
                      )}
                      onClick={() =>
                        onClickCondition(
                          condition.key,
                          item.id === condition.activeItemId ? null : item.id,
                        )
                      }
                    >
                      {item.name}
                      <ChevronRightIcon className="tw:size-6 tw:shrink-0" />
                    </button>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div className="ui-root col-md-8 company-cards">
          <InfiniteScroll
            pageStart={0}
            loadMore={onLoadMore}
            hasMore={hasNextPage}
            loader={<Loader key={0} type="water" />}
            // row/col-md-6 だと行内でカードの高さが揃わないため grid にする。
            // loader は InfiniteScroll の子として並ぶので全幅に伸ばす。
            className="tw:grid tw:grid-cols-1 tw:gap-x-8 tw:gap-y-4 tw:md:grid-cols-2 tw:[&>.loader]:col-span-full"
          >
            {companies.map(company => (
              <a
                href={`/companies/${company.id}`}
                key={company.id}
                className="tw:block"
              >
                <CompanyCard company={company} />
              </a>
            ))}
          </InfiniteScroll>
          {!isLoading && companies.length === 0 && (
            <div className="not-found text-center">
              企業は見つかりませんでした
            </div>
          )}
        </div>
      </div>
    </>
  );
}
